import React, { useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { WebStep } from '@upstart/harmony/lib/legos/constants';
import '@upstart/commander-mui-theme';

import { makeFormSchema, FormSchema } from './schema';
import {
  CheckboxSrcType,
  FormStep,
  ShareDealDialogFormProps,
} from './Content/FormStep';
import PreviewStep, { PreviewStepProps } from './Content/PreviewStep';
import SuccessStep from './Content/SuccessStep';
import messages from './messages.json';
import { hasLeaseTermAndMileageError } from './validation';

export type ShareDealDialogStep = 'form' | 'preview' | 'sent-success';

export interface ShareDealDialogProps<
  StepVal extends CheckboxSrcType = WebStep,
  OfferVal extends CheckboxSrcType = string,
  DateType = Date,
> {
  open: boolean;
  onClose: () => void;
  step: ShareDealDialogStep | undefined;
  onStepChange: (newStep: ShareDealDialogStep) => void;
  onSubmit: (
    formProps: ShareDealDialogFormProps<StepVal, OfferVal, DateType>,
  ) => void;
  formProps: ShareDealDialogFormProps<StepVal, OfferVal, DateType>;
  previewProps: Omit<PreviewStepProps, 'onEditTermsClicked'>;
}

const ShareDealDialog = <
  StepVal extends CheckboxSrcType = string,
  OfferVal extends CheckboxSrcType = string,
  DateType = Date,
>({
  open,
  onClose,
  step = 'form',
  onStepChange,
  onSubmit,
  formProps,
  previewProps,
}: ShareDealDialogProps<StepVal, OfferVal, DateType>) => {
  const { sendConfig } = formProps;
  const validationSchema = useMemo(
    () => makeFormSchema({ shouldSendEmail: sendConfig.shouldSendEmail }),
    [sendConfig.shouldSendEmail],
  );
  const formMethods = useForm<FormSchema>({
    mode: 'onTouched',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      email: sendConfig.email,
      customerMessage: formProps.customerMessage,
    },
  });

  const { handleSubmit } = formMethods;
  const emitFormStep = () => onStepChange('form');
  const emitPreviewStep = () => onStepChange('preview');
  const emitSubmitIfNoErrors = () => {
    // TODO: integrate this validation into react-hook-form
    if (!hasLeaseTermAndMileageError(formProps.stickerConfig)) {
      onSubmit(formProps);
    }
  };
  const getBaseActions = () => (
    <>
      <Button variant="outlined" color="default" onClick={() => onClose()}>
        {messages.dialog.cancel}
      </Button>
      <Button variant="contained" onClick={handleSubmit(emitSubmitIfNoErrors)}>
        {messages.dialog.send}
      </Button>
    </>
  );
  const getSuccessActions = () => (
    <>
      <div />
      <Button variant="contained" color="primary" onClick={() => onClose()}>
        {messages.dialog.close}
      </Button>
    </>
  );

  const dialogContentRef = useRef<HTMLElement>(null);
  const scrollContentToTop = () => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  };
  useEffect(scrollContentToTop, [step]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        {messages.dialog.title}
        <IconButton aria-label={messages.dialog.close} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        ref={dialogContentRef}
        sx={{ width: '800px', paddingX: '40px' }}
      >
        <FormProvider {...formMethods}>
          {step === 'form' && (
            <FormStep
              {...formProps}
              onPreviewStickerClick={handleSubmit(emitPreviewStep)}
            />
          )}
          {step === 'preview' && (
            <PreviewStep {...previewProps} onEditTermsClicked={emitFormStep} />
          )}
          {step === 'sent-success' && <SuccessStep />}
        </FormProvider>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          justifyContent: 'space-between',
        }}
      >
        {step === 'sent-success' ? getSuccessActions() : getBaseActions()}
      </DialogActions>
    </Dialog>
  );
};

export default ShareDealDialog;
